"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseSender = void 0;
const api_1 = require("@opentelemetry/api");
const types_js_1 = require("../../export/statsbeat/types.js");
const index_js_1 = require("./persist/index.js");
const core_1 = require("@opentelemetry/core");
const networkStatsbeatMetrics_js_1 = require("../../export/statsbeat/networkStatsbeatMetrics.js");
const longIntervalStatsbeatMetrics_js_1 = require("../../export/statsbeat/longIntervalStatsbeatMetrics.js");
const types_js_2 = require("../../export/statsbeat/types.js");
const breezeUtils_js_1 = require("../../utils/breezeUtils.js");
const Constants_js_1 = require("../../Declarations/Constants.js");
const DEFAULT_BATCH_SEND_RETRY_INTERVAL_MS = 60_000;
/**
 * Base sender class
 * @internal
 */
class BaseSender {
    persister;
    numConsecutiveRedirects;
    retryTimer;
    networkStatsbeatMetrics;
    customerSDKStatsMetrics;
    longIntervalStatsbeatMetrics;
    statsbeatFailureCount = 0;
    batchSendRetryIntervalMs = DEFAULT_BATCH_SEND_RETRY_INTERVAL_MS;
    isStatsbeatSender;
    disableOfflineStorage;
    constructor(options) {
        this.numConsecutiveRedirects = 0;
        this.disableOfflineStorage = options.exporterOptions.disableOfflineStorage || false;
        if (options.trackStatsbeat) {
            this.networkStatsbeatMetrics = networkStatsbeatMetrics_js_1.NetworkStatsbeatMetrics.getInstance({
                instrumentationKey: options.instrumentationKey,
                endpointUrl: options.endpointUrl,
                disableOfflineStorage: this.disableOfflineStorage,
            });
            this.longIntervalStatsbeatMetrics = longIntervalStatsbeatMetrics_js_1.LongIntervalStatsbeatMetrics.getInstance({
                instrumentationKey: options.instrumentationKey,
                endpointUrl: options.endpointUrl,
                disableOfflineStorage: this.disableOfflineStorage,
            });
            if (!process.env[Constants_js_1.ENV_DISABLE_SDKSTATS]) {
                let exportInterval;
                if (process.env[Constants_js_1.ENV_APPLICATIONINSIGHTS_SDKSTATS_EXPORT_INTERVAL]) {
                    const envValue = process.env[Constants_js_1.ENV_APPLICATIONINSIGHTS_SDKSTATS_EXPORT_INTERVAL];
                    const exportIntervalSeconds = parseInt(envValue, 10);
                    if (!isNaN(exportIntervalSeconds) && exportIntervalSeconds > 0) {
                        exportInterval = exportIntervalSeconds * 1000; // Convert seconds to milliseconds
                    }
                    else {
                        api_1.diag.warn(`Invalid value for APPLICATIONINSIGHTS_SDKSTATS_EXPORT_INTERVAL environment variable: '${envValue}'. Expected a positive number (seconds). Using default export interval.`);
                    }
                }
                // Initialize customer SDK stats metrics asynchronously to avoid circular dependency
                // Only initialize if not already set (e.g., by tests)
                if (!this.customerSDKStatsMetrics) {
                    import("../../export/statsbeat/customerSDKStats.js")
                        .then((module) => module.CustomerSDKStatsMetrics.getInstance({
                        instrumentationKey: options.instrumentationKey,
                        endpointUrl: options.endpointUrl,
                        disableOfflineStorage: this.disableOfflineStorage,
                        networkCollectionInterval: exportInterval,
                    }))
                        .then((metrics) => {
                        this.customerSDKStatsMetrics = metrics;
                        return;
                    })
                        .catch((error) => {
                        api_1.diag.warn("Failed to initialize customer SDK stats metrics:", error);
                    });
                }
            }
        }
        this.persister = new index_js_1.FileSystemPersist(options.instrumentationKey, options.exporterOptions, this.customerSDKStatsMetrics);
        this.retryTimer = null;
        this.isStatsbeatSender = options.isStatsbeatSender || false;
    }
    /**
     * Export envelopes
     */
    async exportEnvelopes(envelopes) {
        api_1.diag.info(`Exporting ${envelopes.length} envelope(s)`);
        if (envelopes.length < 1) {
            return { code: core_1.ExportResultCode.SUCCESS };
        }
        try {
            const startTime = new Date().getTime();
            const { result, statusCode } = await this.send(envelopes);
            const endTime = new Date().getTime();
            const duration = endTime - startTime;
            this.numConsecutiveRedirects = 0;
            if (statusCode === 200) {
                // Success -- @todo: start retry timer
                if (!this.retryTimer) {
                    this.retryTimer = setTimeout(() => {
                        this.retryTimer = null;
                        this.sendFirstPersistedFile();
                    }, this.batchSendRetryIntervalMs);
                    this.retryTimer.unref();
                }
                // If we are not exporting statsbeat and statsbeat is not disabled -- count success
                if (!this.isStatsbeatSender) {
                    this.networkStatsbeatMetrics?.countSuccess(duration);
                    this.customerSDKStatsMetrics?.countSuccessfulItems(envelopes);
                }
                return { code: core_1.ExportResultCode.SUCCESS };
            }
            else if (statusCode && (0, breezeUtils_js_1.isRetriable)(statusCode)) {
                // Failed -- persist failed data
                if (statusCode === 429 || statusCode === 439) {
                    if (!this.isStatsbeatSender) {
                        this.networkStatsbeatMetrics?.countThrottle(statusCode);
                        this.customerSDKStatsMetrics?.countRetryItems(envelopes, statusCode);
                    }
                    return {
                        code: core_1.ExportResultCode.SUCCESS,
                    };
                }
                if (result) {
                    api_1.diag.info(result);
                    const breezeResponse = JSON.parse(result);
                    const filteredEnvelopes = [];
                    // Create a list of successful envelopes by filtering out the failed ones for customer SDK Stats
                    const successfulEnvelopes = [...envelopes];
                    // If we have a partial success, count the succeeded envelopes
                    if (breezeResponse.itemsAccepted > 0 && statusCode === 206 && !this.isStatsbeatSender) {
                        this.networkStatsbeatMetrics?.countSuccess(duration);
                    }
                    // Figure out if we need to either retry or count failures
                    if (breezeResponse.errors) {
                        breezeResponse.errors.forEach((error) => {
                            // Mark as undefined so we don't process them in countSuccessfulEnvelopes
                            successfulEnvelopes[error.index] = undefined;
                            // Add to retry list if status code is retriable and not a sampling rejection
                            // Sampling rejections should not be retried as the server will always reject these items
                            if (error.statusCode &&
                                (0, breezeUtils_js_1.isRetriable)(error.statusCode) &&
                                !(0, breezeUtils_js_1.isSamplingRejection)(error)) {
                                filteredEnvelopes.push(envelopes[error.index]);
                            }
                        });
                    }
                    // If we have a partial success, count the succeeded envelopes
                    if (breezeResponse.itemsAccepted > 0) {
                        // Count only the successful envelopes (non-undefined)
                        if (!this.isStatsbeatSender) {
                            this.networkStatsbeatMetrics?.countSuccess(duration);
                            this.customerSDKStatsMetrics?.countSuccessfulItems(envelopes);
                        }
                    }
                    if (filteredEnvelopes.length > 0) {
                        if (!this.isStatsbeatSender) {
                            this.networkStatsbeatMetrics?.countRetry(statusCode);
                            this.customerSDKStatsMetrics?.countRetryItems(envelopes, statusCode);
                        }
                        // calls resultCallback(ExportResult) based on result of persister.push
                        return await this.persist(filteredEnvelopes);
                    }
                    // Failed -- not retriable
                    if (!this.isStatsbeatSender) {
                        this.networkStatsbeatMetrics?.countFailure(duration, statusCode);
                        // Count dropped items for customer SDK Stats for non-retriable status codes
                        const filteredSuccessfulEnvelopes = successfulEnvelopes.filter(Boolean);
                        this.customerSDKStatsMetrics?.countDroppedItems(filteredSuccessfulEnvelopes, statusCode);
                    }
                    return this.buildExportResult({
                        code: core_1.ExportResultCode.FAILED,
                    });
                }
                else {
                    // calls resultCallback(ExportResult) based on result of persister.push
                    if (!this.isStatsbeatSender) {
                        this.networkStatsbeatMetrics?.countRetry(statusCode);
                        this.customerSDKStatsMetrics?.countRetryItems(envelopes, statusCode);
                    }
                    return await this.persist(envelopes);
                }
            }
            else {
                // Failed -- not retriable
                if (this.networkStatsbeatMetrics && !this.isStatsbeatSender) {
                    if (statusCode) {
                        this.networkStatsbeatMetrics.countFailure(duration, statusCode);
                        this.customerSDKStatsMetrics?.countDroppedItems(envelopes, statusCode);
                    }
                }
                else {
                    // Handles all other status codes or client exceptions for Statsbeat
                    this.incrementStatsbeatFailure();
                    this.customerSDKStatsMetrics?.countDroppedItems(envelopes, types_js_2.DropCode.CLIENT_EXCEPTION);
                }
                return this.buildExportResult({
                    code: core_1.ExportResultCode.FAILED,
                });
            }
        }
        catch (error) {
            const restError = error;
            if (restError.statusCode &&
                (restError.statusCode === 307 || // Temporary redirect
                    restError.statusCode === 308)) {
                // Permanent redirect
                this.numConsecutiveRedirects++;
                // To prevent circular redirects
                if (this.numConsecutiveRedirects < 10) {
                    if (restError.response && restError.response.headers) {
                        const location = restError.response.headers.get("location");
                        if (location) {
                            // Update sender URL
                            this.handlePermanentRedirect(location);
                            // Send to redirect endpoint as HTTPs library doesn't handle redirect automatically
                            return this.exportEnvelopes(envelopes);
                        }
                    }
                }
                else {
                    const redirectError = new Error("Circular redirect");
                    if (!this.isStatsbeatSender) {
                        this.networkStatsbeatMetrics?.countException(redirectError);
                        this.customerSDKStatsMetrics?.countDroppedItems(envelopes, types_js_2.DropCode.CLIENT_EXCEPTION, redirectError.message, types_js_1.ExceptionType.CLIENT_EXCEPTION);
                    }
                    return this.buildExportResult({ code: core_1.ExportResultCode.FAILED, error: redirectError });
                }
            }
            else if (restError.statusCode &&
                (0, breezeUtils_js_1.isRetriable)(restError.statusCode) &&
                !this.isStatsbeatSender) {
                this.networkStatsbeatMetrics?.countRetry(restError.statusCode);
                this.customerSDKStatsMetrics?.countRetryItems(envelopes, restError.statusCode);
                return this.persist(envelopes);
            }
            else if (restError.statusCode === 400 &&
                restError.message.includes("Invalid instrumentation key")) {
                // Invalid instrumentation key, shutdown statsbeat, fail silently
                this.shutdownStatsbeat();
                return { code: core_1.ExportResultCode.SUCCESS };
            }
            else if (restError.statusCode &&
                this.isStatsbeatSender &&
                (0, types_js_2.isStatsbeatShutdownStatus)(restError.statusCode)) {
                // If the status code is a shutdown status code for statsbeat, shutdown statsbeat and fail silently
                this.incrementStatsbeatFailure();
                return { code: core_1.ExportResultCode.SUCCESS };
            }
            // For retriable REST errors
            if (this.isRetriableRestError(restError) && !this.isStatsbeatSender) {
                if (this.customerSDKStatsMetrics?.isTimeoutError(restError) && !this.isStatsbeatSender) {
                    this.customerSDKStatsMetrics?.countRetryItems(envelopes, types_js_2.RetryCode.CLIENT_TIMEOUT, "timeout_exception", types_js_1.ExceptionType.TIMEOUT_EXCEPTION);
                    api_1.diag.error("Request timed out. Error message:", restError.message);
                }
                else if (restError.statusCode) {
                    this.networkStatsbeatMetrics?.countRetry(restError.statusCode);
                    this.customerSDKStatsMetrics?.countRetryItems(envelopes, restError.statusCode);
                }
                api_1.diag.error("Retrying due to transient client side error. Error message:", restError.message);
                return this.persist(envelopes);
            }
            // For non-retriable REST errors or client exceptions
            if (!this.isStatsbeatSender) {
                this.networkStatsbeatMetrics?.countException(restError);
                this.customerSDKStatsMetrics?.countDroppedItems(envelopes, types_js_2.DropCode.CLIENT_EXCEPTION, restError.message);
                api_1.diag.error("Envelopes could not be exported and are not retriable. Error message:", restError.message);
            }
            return this.buildExportResult({ code: core_1.ExportResultCode.FAILED, error: restError });
        }
    }
    /**
     * Persist envelopes to disk
     */
    async persist(envelopes) {
        try {
            const success = await this.persister.push(envelopes);
            return success
                ? { code: core_1.ExportResultCode.SUCCESS }
                : this.buildExportResult({
                    code: core_1.ExportResultCode.FAILED,
                    error: new Error("Failed to persist envelope in disk."),
                });
        }
        catch (ex) {
            if (!this.isStatsbeatSender) {
                this.networkStatsbeatMetrics?.countWriteFailure();
                if (this.disableOfflineStorage && envelopes) {
                    this.customerSDKStatsMetrics?.countDroppedItems(envelopes, types_js_2.DropCode.CLIENT_STORAGE_DISABLED);
                }
            }
            return this.buildExportResult({ code: core_1.ExportResultCode.FAILED, error: ex });
        }
    }
    /**
     * Disable collection of statsbeat metrics after max failures
     */
    incrementStatsbeatFailure() {
        this.statsbeatFailureCount++;
        if (this.statsbeatFailureCount > types_js_2.MAX_STATSBEAT_FAILURES) {
            this.shutdownStatsbeat();
        }
    }
    /**
     * Shutdown statsbeat metrics
     */
    shutdownStatsbeat() {
        if (this.networkStatsbeatMetrics) {
            this.networkStatsbeatMetrics.shutdown();
        }
        if (this.longIntervalStatsbeatMetrics) {
            this.longIntervalStatsbeatMetrics?.shutdown();
        }
        if (this.customerSDKStatsMetrics) {
            this.customerSDKStatsMetrics.shutdown();
        }
        this.statsbeatFailureCount = 0;
    }
    async sendFirstPersistedFile() {
        const envelopes = (await this.persister.shift());
        try {
            if (envelopes) {
                await this.send(envelopes);
            }
        }
        catch (err) {
            if (!this.isStatsbeatSender) {
                this.networkStatsbeatMetrics?.countReadFailure();
            }
            api_1.diag.warn(`Failed to fetch persisted file`, err);
        }
    }
    isRetriableRestError(error) {
        const restErrorTypes = Object.values(Constants_js_1.RetriableRestErrorTypes);
        if (error && error.code && restErrorTypes.includes(error.code)) {
            return true;
        }
        return false;
    }
    // Silence noisy failures from statsbeat OTel metric readers unless logging is explicitly enabled
    buildExportResult(result) {
        const shouldSurfaceStatsbeatFailures = !!process.env[Constants_js_1.ENV_APPLICATIONINSIGHTS_SDK_STATS_LOGGING];
        if (this.isStatsbeatSender && result.code === core_1.ExportResultCode.FAILED) {
            return shouldSurfaceStatsbeatFailures ? result : { code: core_1.ExportResultCode.SUCCESS };
        }
        return result;
    }
}
exports.BaseSender = BaseSender;
//# sourceMappingURL=baseSender.js.map